import React, { useState, useEffect, useCallback } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { UserPlus, Trash2, Users, Loader2, Link2, Mail, Target } from "lucide-react";
import { StudentProgress } from "@/components/StudentProgress";

export default function TeamSheet({ open, onOpenChange }) {
  const [team, setTeam] = useState(null);
  const [board, setBoard] = useState([]);
  const [feed, setFeed] = useState([]);
  const [email, setEmail] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviting, setInviting] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [goalInput, setGoalInput] = useState("");
  const [savingGoal, setSavingGoal] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/team");
      setTeam(data);
      setGoalInput(data.challenge_goal ? String(data.challenge_goal) : "");
      try {
        const f = await api.get("/team/feed");
        setFeed(f.data.feed);
      } catch (e) { /* ignore */ }
      if (data.is_owner) {
        const lb = await api.get("/team/leaderboard");
        setBoard(lb.data.leaderboard);
      }
    } catch (e) {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const emailInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    setEmailing(true);
    try {
      const { data } = await api.post("/team/invite-email", { email: inviteEmail.trim() });
      toast.success(data.message);
      setInviteEmail("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send invite");
    } finally {
      setEmailing(false);
    }
  };

  const invite = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setInviting(true);
    try {
      const { data } = await api.post("/team/invite", { email: email.trim() });
      toast.success(data.message);
      setEmail("");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not invite");
    } finally {
      setInviting(false);
    }
  };

  const saveGoal = async (e) => {
    e.preventDefault();
    const goal = parseInt(goalInput, 10);
    if (isNaN(goal) || goal < 0) return;
    setSavingGoal(true);
    try {
      await api.post("/team/challenge", { goal });
      toast.success(goal > 0 ? "Weekly class goal set!" : "Challenge cleared");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not set goal");
    } finally {
      setSavingGoal(false);
    }
  };

  const remove = async (userId) => {    try {
      await api.post("/team/remove", { user_id: userId });
      toast.success("Member removed");
      load();
    } catch (e) {
      toast.error("Could not remove");
    }
  };

  const seats = team?.seats || 0;
  const used = team?.used || 0;
  const goal = team?.challenge_goal || 0;
  const progress = team?.challenge_progress || 0;
  const goalPct = goal > 0 ? Math.min(100, (progress / goal) * 100) : 0;
  const joinLink = team?.join_token ? `${window.location.origin}/join/${team.join_token}` : "";

  const copyJoin = async () => {
    try {
      await navigator.clipboard.writeText(joinLink);
      toast.success("Invite link copied!");
    } catch (e) {
      toast.error("Copy failed");
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="bg-[#0b0c10] border-white/10 w-full sm:max-w-md overflow-y-auto scroll-thin">
        <SheetHeader>
          <SheetTitle className="font-heading text-3xl uppercase tracking-tight text-left flex items-center gap-2">
            <Users className="w-6 h-6 amber-text" /> My <span className="amber-text">Team</span>
          </SheetTitle>
          <SheetDescription className="text-slate-500 text-left">
            Share Pro access with your students. They just need a free BeatFlow account.
          </SheetDescription>
        </SheetHeader>

        {team && team.is_owner ? (
          <div className="mt-6">
            <div className="rounded-xl bg-white/[0.03] border border-white/10 p-4 mb-5">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-slate-400 font-tab">Seats used</span>
                <span className="font-heading text-xl" data-testid="team-seats">{used} / {seats}</span>
              </div>
              <Progress value={(used / seats) * 100} className="h-2" />
            </div>

            <form onSubmit={invite} className="flex gap-2 mb-5">
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="student@email.com"
                className="bg-[#12161f] border-white/10"
                data-testid="team-invite-email"
              />
              <Button
                type="submit"
                disabled={inviting || used >= seats}
                className="bg-[#ffab00] text-black hover:bg-[#ffc233] font-semibold shrink-0"
                data-testid="team-invite-button"
              >
                {inviting ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
              </Button>
            </form>

            <div className="flex gap-2 mb-3">
              <Input value={joinLink} readOnly className="bg-[#0b0c10] border-white/10 font-tab text-xs" data-testid="team-join-link" />
              <Button variant="outline" onClick={copyJoin} className="border-white/15 bg-white/5 shrink-0" data-testid="team-join-copy">
                <Link2 className="w-4 h-4" />
              </Button>
            </div>

            <form onSubmit={emailInvite} className="flex gap-2 mb-6">
              <Input type="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} placeholder="Email the join link…" className="bg-[#12161f] border-white/10" data-testid="invite-email-input" />
              <Button type="submit" disabled={emailing} variant="outline" className="border-white/15 bg-white/5 shrink-0" data-testid="invite-email-send-button">
                {emailing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
              </Button>
            </form>

            <div className="rounded-xl bg-gradient-to-br from-[#ffab00]/10 to-transparent border border-[#ffab00]/20 p-4 mb-6" data-testid="class-challenge">
              <div className="flex items-center gap-2 mb-2">
                <Target className="w-4 h-4 amber-text" />
                <h4 className="font-heading text-sm uppercase tracking-widest amber-text">Weekly class challenge</h4>
              </div>
              {goal > 0 ? (
                <>
                  <div className="flex items-end justify-between mb-1.5">
                    <span className="text-sm text-slate-400 font-tab">Tabs this week</span>
                    <span className="font-heading text-xl" data-testid="challenge-progress-text">
                      {progress} <span className="text-slate-500 text-sm">/ {goal}</span>
                    </span>
                  </div>
                  <Progress value={goalPct} className="h-2.5" data-testid="challenge-progress-bar" />
                  <p className="text-xs mt-2 text-slate-500">
                    {progress >= goal
                      ? "🎉 Goal smashed! Set a bigger one below."
                      : `${goal - progress} more to beat the goal — race on!`}
                  </p>
                </>
              ) : (
                <p className="text-xs text-slate-500 mb-2">Set a weekly tab-count goal for the whole class to race toward.</p>
              )}
              <form onSubmit={saveGoal} className="flex gap-2 mt-3">
                <Input
                  type="number"
                  min="0"
                  value={goalInput}
                  onChange={(e) => setGoalInput(e.target.value)}
                  placeholder="e.g. 50 tabs"
                  className="bg-[#12161f] border-white/10"
                  data-testid="challenge-goal-input"
                />
                <Button
                  type="submit"
                  disabled={savingGoal}
                  variant="outline"
                  className="border-[#ffab00]/40 text-[#ffab00] bg-[#ffab00]/5 shrink-0"
                  data-testid="challenge-set-button"
                >
                  {savingGoal ? <Loader2 className="w-4 h-4 animate-spin" /> : "Set goal"}
                </Button>
              </form>
            </div>

            <StudentProgress open={open} />

            {board.length > 0 && (
              <div className="mb-6">
                <h4 className="font-heading text-sm uppercase tracking-widest text-[#ffab00] mb-2">This week's leaderboard</h4>
                <div className="space-y-1.5" data-testid="team-leaderboard">
                  {board.map((r, i) => (
                    <div key={r.id} className="flex items-center justify-between rounded-lg bg-white/[0.03] px-3 py-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className={`font-heading text-base w-5 ${i === 0 ? "amber-text" : "text-slate-500"}`}>#{i + 1}</span>
                        <span className="text-sm truncate">{r.name}</span>
                      </div>
                      <span className="font-tab text-sm cyan-text">{r.tabs}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {feed.length > 0 && (
              <div className="mb-6" data-testid="class-feed">
                <h4 className="font-heading text-sm uppercase tracking-widest text-[#00f0ff] mb-2">Class feed</h4>
                <div className="space-y-1.5 max-h-48 overflow-y-auto scroll-thin">
                  {feed.map((p, i) => (
                    <div key={i} className="flex items-center justify-between rounded-lg bg-white/[0.03] px-3 py-2">
                      <span className="text-sm truncate">{p.name}</span>
                      <span className="font-tab text-xs text-slate-400 shrink-0">
                        {p.minutes}m{p.streak > 0 ? ` · ${p.streak}w streak` : ""}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-2">
              {(team.members || []).length === 0 && (
                <p className="text-sm text-slate-500 text-center py-8">No students yet. Invite one above or share the link.</p>
              )}
              {(team.members || []).map((m) => (
                <div
                  key={m.id}
                  className="flex items-center justify-between rounded-lg bg-white/[0.03] border border-white/10 px-3 py-2.5"
                  data-testid={`team-member-${m.id}`}
                >
                  <div className="min-w-0">
                    <p className="text-sm font-semibold truncate">{m.name || m.email}</p>
                    <p className="text-xs text-slate-500 truncate">{m.email}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <div className="text-right">
                      <div className="font-heading text-lg leading-none cyan-text" data-testid={`member-tabs-${m.id}`}>{m.tabs_this_month ?? 0}</div>
                      <div className="text-[9px] text-slate-500 font-tab uppercase">tabs / mo</div>
                    </div>
                    <Button
                      size="icon" variant="outline"
                      onClick={() => remove(m.id)}
                      className="h-8 w-8 border-white/15 text-[#FF3366]"
                      data-testid={`team-remove-${m.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="mt-10 text-center text-slate-500">
            <Users className="w-10 h-10 mx-auto mb-3 opacity-40" />
            <p className="text-sm">
              {team && team.member
                ? "You're part of a team plan — enjoy Pro access!"
                : "You don't have a team plan yet. Grab the Teacher plan to invite students."}
            </p>
            {team && team.member && feed.length > 0 && (
              <div className="mt-6 text-left" data-testid="class-feed-member">
                <h4 className="font-heading text-sm uppercase tracking-widest text-[#00f0ff] mb-2">Class feed</h4>
                <div className="space-y-1.5 max-h-56 overflow-y-auto scroll-thin">
                  {feed.map((p, i) => (
                    <div key={i} className="flex items-center justify-between rounded-lg bg-white/[0.03] px-3 py-2">
                      <span className="text-sm truncate text-slate-300">{p.name}</span>
                      <span className="font-tab text-xs text-slate-400 shrink-0">
                        {p.minutes}m{p.streak > 0 ? ` · ${p.streak}w streak` : ""}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
